import {
  type messageCallback,
  type InboundMessage,
  type RealtimeChannel,
  type MessageFilter,
} from 'ably';
import { useCallback, useEffect, useRef } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { useAblyRealtime } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/hooks/useAblyRealtime';
import { type AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';
import { getInactivityPublisherSingleton } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/utils/ablyInactivityPublisherSingleton';
import { InactivityPublisher } from 'hb-react/shared/utils/InactivityPublisher';

// Using a timeout to ensure the toast will be shown only once even
// if multiple instances of this default resubscription handler are
// used throughout the page. Since inactivity is a global event, we
// know that the inactivity handlers will all be called at the same time.
let toastTimeoutId: NodeJS.Timeout | null = null;
const showInactivityToast = () => {
  if (toastTimeoutId) {
    clearTimeout(toastTimeoutId);
  }

  toastTimeoutId = setTimeout(
    () =>
      toast(
        s__(
          'Ably|Looks like your session was inactive for a bit — please refresh the page to continue with the latest information.',
        ),
        {
          duration: 15000,
        },
      ),
    300,
  );
};

export interface UseSubscribeToAblyChannelParams {
  autoConnect?: boolean;
  channel: string;
  enabled?: boolean;
  filter?: Array<string> | MessageFilter | string;
  onMessage: messageCallback<InboundMessage>;
  onResubscribedAfterInactivity?: (() => void) | null;
  onUnsubscribedDueToInactivity?: () => void;
  tokenType: AblyTokenType;
}

export interface UseSubscribeToAblyChannelReturn {
  subscribe: () => Promise<void>;
  unsubscribe: () => void;
}

type UseSubscribeToAblyChannel = (
  params: UseSubscribeToAblyChannelParams,
) => UseSubscribeToAblyChannelReturn;

/**
 * Utility hook that manages connection to the ably client and subscription to a channel.
 *
 * By default it will automatically connect and subscribe to the channel when the component mounts
 * but you can disable this behavior by setting `autoConnect` to false and control the
 * subscription manually using the `subscribe` and `unsubscribe` functions returned by the hook.
 *
 * If you want to receive only a subset of messages from the channel, you can use the `filter` parameter.
 *
 * In order to save costs, ably will automatically be disconnected after some time if the page becomes
 * inactive. While ably is disconnected, you WILL NOT receive any of messages published to the channel and
 * they WILL NOT be available once the connection is re-established. It is your responsibility to implement
 * proper fallback mechanisms via the onUnsubscribedDueToInactivity and onResubscribedAfterInactivity handlers
 * (by default we will show a toast prompting the user to refresh the page when we become active again after
 * a period of inactivity).
 *
 * @param {AblyTokenType} tokenType - Determines which authUrl to use
 * @param {string} channel The name of the channel to subscribe to
 * @param {messageCallback<InboundMessage>} onMessage The message handler that will be called when a message is received
 * @param {boolean} [enabled=true] Let you manually enable/disable the subscription
 * @param {boolean} [autoConnect=true] If TRUE, the subscription will be automatically started when the component mounts
 * @param {string | string[] | MessageFilter} filter The message handler will only be called for messages that match the filter
 *        (see https://ably.com/docs/channels?lang=javascript#subscription-filters)
 * @param {() => void} onUnsubscribedDueToInactivity Called when ably is disconnected due to
 *        the page becoming inactive for too long
 * @param {(() => void) | null} [onResubscribedAfterInactivity=showInactivityToast] Called when ably is reconnected when
 *        an inactive page becomes active again
 * @returns {UseSubscribeToAblyChannelReturn}
 * - subscribe - Manually subscribe to the channel and register the provided message handler
 * - unsubscribe - Manually unsubscribe from the channel and detach the channel if there are no more subscriptions
 */
export const useSubscribeToAblyChannel: UseSubscribeToAblyChannel = ({
  tokenType,
  channel,
  onMessage,
  enabled = true,
  autoConnect = true,
  filter,
  onUnsubscribedDueToInactivity,
  onResubscribedAfterInactivity = showInactivityToast,
}) => {
  const { flagEvAblyInactivityTracker } = useFeatureFlag();
  const inactivityTrackerRef = useRef<InactivityPublisher>();

  const { getAblyRealtime, isAblyEnabled, closeIfUnused } = useAblyRealtime({
    tokenType,
  });

  const channelRef = useRef<RealtimeChannel | null>(null);
  const isPaused = useRef<boolean>(false);

  /**
   * Subscribes to the channel with the given filter and message handler
   */
  const subscribe = useCallback(async () => {
    if (!isAblyEnabled || !enabled) {
      return;
    }

    // Trying to subscribe while the tab is inactive.
    // Mark the subscription as paused so that it can be resumed when the page becomes active
    if (!inactivityTrackerRef.current?.isActive) {
      isPaused.current = true;

      return;
    }

    const realtime = await getAblyRealtime();

    channelRef.current = realtime.channels.get(channel);
    if (filter === undefined) {
      await channelRef.current.subscribe(onMessage);
    } else if (typeof filter === 'string') {
      await channelRef.current.subscribe(filter, onMessage);
    } else if (Array.isArray(filter)) {
      await channelRef.current.subscribe(filter, onMessage);
    } else {
      await channelRef.current.subscribe(filter, onMessage);
    }
  }, [channel, enabled, filter, getAblyRealtime, isAblyEnabled, onMessage]);

  /**
   * Unsubscribes this specific message handler from the channel and detaches
   * the channel if there are no more subscriptions to it
   */
  const unsubscribe = useCallback(() => {
    isPaused.current = false;

    if (!channelRef.current) {
      closeIfUnused();

      return;
    }

    channelRef.current?.unsubscribe(onMessage);

    // If all the subscriptions have been unsubscribed, detach the channel to avoid
    // unnecessary costs
    // @ts-expect-error Ably types are not up-to-date
    if (channelRef.current?.subscriptions.any.length === 0) {
      if (channelRef.current.state === 'attached') {
        try {
          channelRef.current?.detach();
        } catch {
          // Given the asynchronous nature of detaching the channel, and the fact we can have
          // multiple useSubscribeToAblyChannel hooks using the same channel, it is unfortunately
          // possible for race conditions to happen and detach to throw an error.
          // We can safely ignore the error.
        }
      }
    }

    channelRef.current = null;

    closeIfUnused();
  }, [closeIfUnused, onMessage]);

  const handleInactive = useCallback(() => {
    // If the channel was not currently subscribed, we don't need to pause it
    // and will not want it to subscribe automatically if the page becomes active
    if (!channelRef.current) {
      return;
    }

    unsubscribe();

    onUnsubscribedDueToInactivity?.();

    // WARNING: Call AFTER unsubscribe above.
    isPaused.current = true;
  }, [onUnsubscribedDueToInactivity, unsubscribe]);

  const handleActive = useCallback(async () => {
    // If the channel was not paused, we don't need to resume it.
    // It's likely the channel is manually controlled and was not subscribed
    // when the page became inactive
    if (!isPaused.current) {
      return;
    }

    await subscribe();

    onResubscribedAfterInactivity?.();

    isPaused.current = false;
  }, [onResubscribedAfterInactivity, subscribe]);

  // Register the local handlers with the inactivity tracker, responsible
  // for pausing the subscription when the page becomes inactive and resuming
  // it when the page becomes active
  useEffect(() => {
    if (!isAblyEnabled) {
      return;
    }

    if (!flagEvAblyInactivityTracker) {
      inactivityTrackerRef.current = new InactivityPublisher([]);

      return;
    }

    const tracker = getInactivityPublisherSingleton();

    inactivityTrackerRef.current = tracker;
    tracker.subscribeToOnActive(handleActive);
    tracker.subscribeToOnInactive(handleInactive);

    return () => {
      tracker.unsubscribeFromOnActive(handleActive);
      tracker.unsubscribeFromOnInactive(handleInactive);
    };
  }, [
    flagEvAblyInactivityTracker,
    handleActive,
    handleInactive,
    isAblyEnabled,
  ]);

  useEffect(() => {
    if (!isAblyEnabled) {
      return;
    }

    if (autoConnect) {
      subscribe();
    }

    return () => {
      unsubscribe();
    };
  }, [autoConnect, isAblyEnabled, subscribe, unsubscribe]);

  return { subscribe, unsubscribe };
};
